import { observer } from 'mobx-react-lite'
import { useEffect, useState } from 'react'
import type { List } from 'app/generated/graphql'
import { moveListItem } from 'app/lib/listApi'
import { notifyError } from 'app/services/notify.client'
import { useContactStore } from 'app/stores/StoreContext'
import type { ContactJson } from 'app/types/entities'
import { logError } from 'lib/logging'
import { recordPlacement } from 'lib/listPlacements'
import { defaultStageIdOf } from 'lib/practiceLists'

// Keyed on the contact's id so a new contact gets a fresh `StageSelect`, never a re-render of the old
// one: a write still in flight for the previous contact lands on an unmounted instance and touches nothing.
export function ListItemStageCell({
  contact,
  list,
  stageId,
  onMoved,
}: {
  contact: ContactJson
  list: List
  stageId: string | undefined
  /** Called once the write is confirmed, so the owner of the stage can keep it. */
  onMoved?: (stageId: string) => void
}) {
  return <StageSelect key={contact.id ?? `no-id`} contact={contact} list={list} stageId={stageId} onMoved={onMoved} />
}

// Reads the member's stage from the store so a refetch re-renders the cell, which a column's render
// function cannot do on its own.
export const ListMemberStageCell = observer(function ListMemberStageCell({
  contact,
  list,
}: {
  contact: ContactJson
  list: List
}) {
  const contactStore = useContactStore()
  return (
    <ListItemStageCell
      contact={contact}
      list={list}
      stageId={contactStore.memberStages.get(contact.id!)}
      onMoved={(next) => contactStore.setMemberStage(contact.id!, next)}
    />
  )
})

function StageSelect({
  contact,
  list,
  stageId,
  onMoved,
}: {
  contact: ContactJson
  list: List
  stageId: string | undefined
  onMoved?: (stageId: string) => void
}) {
  const [optimistic, setOptimistic] = useState<string | undefined>()
  const [saving, setSaving] = useState(false)

  // Once the member read agrees with what this cell wrote, the write is confirmed and the read is the truth again.
  useEffect(() => {
    if (optimistic !== undefined && stageId === optimistic) setOptimistic(undefined)
  }, [stageId, optimistic])

  const stages = list.stages ?? []
  const value = optimistic ?? stageId
  // A member with no stage, or one whose stage was deleted, sits in the default stage, as the board shows them.
  const known = stages.some((stage) => stage.id === value)
  const shown = known ? value : defaultStageIdOf(list)

  const onChange = async (next: string) => {
    // A failed write goes back to what the cell showed before this change, not further.
    const shownBefore = optimistic
    setOptimistic(next)
    setSaving(true)
    try {
      await moveListItem(list.id, contact.id!, next)
      // The board reads the same overlay, so this move shows there before the search index catches up.
      recordPlacement(list.id, contact.id!, { stage: next, item: contact, at: Date.now() })
      onMoved?.(next)
    } catch (error) {
      logError(`Moving a list member failed`, error)
      setOptimistic(shownBefore)
      notifyError(`Couldn't save that move.`)
    }
    setSaving(false)
  }

  // The padding sits on a wrapper: a native select draws its chevron at its own border edge.
  return (
    <div className="px-2">
      <select
        aria-label={`Stage for ${contact.name ?? `this contact`}`}
        className="w-full cursor-pointer border-0 bg-transparent text-body-xs text-default outline-none disabled:cursor-not-allowed disabled:opacity-50"
        value={shown ?? ``}
        disabled={saving}
        onClick={(event) => event.stopPropagation()}
        onChange={(event) => onChange(event.target.value)}
      >
        {shown === undefined && <option value="">No stage</option>}
        {stages.map((stage) => (
          <option key={stage.id} value={stage.id}>
            {stage.name}
          </option>
        ))}
      </select>
    </div>
  )
}
